import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { Quote } from "lucide-react";

/**
 * Phase 7: public aggregate rating + "volunteer voices" strip for org pages.
 * Privacy: feedback wasn't collected with public display in mind, so the
 * backend returns FIRST NAMES ONLY (or "A volunteer" for private profiles)
 * and short excerpts; reported items are excluded server-side.
 */
export function OrgVoices({ orgId }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.orgPublicFeedback(orgId).then(setD).catch(() => setD(null));
  }, [orgId]);

  if (!d) return null;

  return (
    <div data-testid="org-voices-section" className="bg-white border border-neutral-200 rounded-2xl p-6 mt-8">
      <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Volunteer voices</div>
      {d.count === 0 ? (
        <div data-testid="org-voices-empty" className="text-sm text-neutral-500 mt-3">
          No volunteer feedback yet — it appears here once volunteers review this organisation's events.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <StarRating value={d.average_rating || 0} readonly size={22} />
            <b data-testid="org-voices-avg" className="text-forest">{d.average_rating?.toFixed(1)}</b>
            <span data-testid="org-voices-count" className="text-sm text-neutral-500">
              from {d.count} volunteer review{d.count === 1 ? "" : "s"}
            </span>
          </div>
          {d.voices.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
              {d.voices.map((v, i) => (
                <figure key={v.excerpt} data-testid={`org-voice-${i}`} className="bg-cream/60 border border-forest/10 rounded-xl p-4">
                  <Quote className="w-4 h-4 text-terracotta/70" />
                  <blockquote className="text-sm text-neutral-700 mt-2">"{v.excerpt}"</blockquote>
                  <figcaption className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
                    <StarRating value={v.rating} readonly size={12} />
                    <span>— {v.author_display}{v.opportunity_title ? `, ${v.opportunity_title}` : ""}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
          <p className="text-[11px] text-neutral-400 mt-4">
            First names only, from volunteers who took part in this organisation's events.
          </p>
        </>
      )}
    </div>
  );
}
